import { useState } from 'react';
import { Modal, Button, Space, App as AntdApp, Divider, Statistic, Row, Col } from 'antd';
import { BarcodeOutlined, DollarOutlined } from '@ant-design/icons';
import type { Product } from '../../../shared/types';
import { useCreateSubscription } from '../hooks/useSubscription';

interface ProductCheckoutModalProps {
  product: Product | null;
  open: boolean;
  onClose: () => void;
}

export default function ProductCheckoutModal({ product, open, onClose }: ProductCheckoutModalProps) {
  const { message } = AntdApp.useApp();
  const [isProcessing, setIsProcessing] = useState(false);
  const createSubscription = useCreateSubscription();

  // Check if product is FREE
  const isFreeProduct = !product || ((product.monthly_price || 0) + (product.setup_fee || 0)) <= 0;

  const handleFinish = async () => {
    if (!product) return;
    
    // Prevent double-clicks
    if (isProcessing) {
      console.log('Already processing, ignoring click');
      return;
    }
    
    setIsProcessing(true);

    try {
      console.log('Creating subscription for:', product.name);
      
      const payload = {
        product_id: product.id,
        billing_type: (isFreeProduct ? 'FREE' : 'BOLETO') as 'CREDIT_CARD' | 'BOLETO' | 'PIX' | 'UNDEFINED' | 'FREE',
      };

      const result = await createSubscription.mutateAsync(payload);

      if (result.is_free) {
        message.success('Produto ativado com sucesso!');
        handleClose();
      } else {
        const paymentUrl = result.payment_url || result.payment_link_url;
        if (paymentUrl) {
          message.success('Abrindo boleto para pagamento...');
          window.open(paymentUrl, '_blank');
          handleClose();
        } else {
          throw new Error('URL de pagamento não foi gerada');
        }
      }
    } catch (error: any) {
      console.error('Error creating subscription:', error);
      message.error(error?.message || 'Erro ao contratar produto');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    if (isProcessing) return;
    onClose();
    setIsProcessing(false);
  };

  if (!product) return null;

  const monthlyValue = product.monthly_price || 0;
  const setupFee = product.setup_fee || 0;
  const firstPayment = monthlyValue + setupFee;

  return (
    <Modal
      title={`Contratar: ${product.name}`}
      open={open}
      onCancel={handleClose}
      width={600}
      footer={null}
      closable={!isProcessing}
      maskClosable={!isProcessing}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Resumo do Produto */}
        <div style={{ padding: 16, background: '#f0f5ff', borderRadius: 8, border: '1px solid #d6e4ff' }}>
          <h3 style={{ marginTop: 0 }}>Resumo da Contratação</h3>
          <Divider style={{ margin: '12px 0' }} />
          
          {isFreeProduct ? (
            <div style={{ textAlign: 'center', color: '#52c41a' }}>
              <p style={{ fontSize: 18, fontWeight: 'bold' }}>Produto Gratuito</p>
              <p>Este produto não possui custos e será ativado imediatamente.</p>
            </div>
          ) : (
            <Row gutter={16}>
              <Col span={12}>
                <Statistic 
                  title="1º Pagamento" 
                  value={firstPayment} 
                  precision={2} 
                  prefix="R$"
                  valueStyle={{ color: '#1890ff' }}
                />
                {setupFee > 0 && (
                  <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                    Mensalidade (R$ {monthlyValue.toFixed(2)}) + Setup (R$ {setupFee.toFixed(2)})
                  </div>
                )}
              </Col>
              <Col span={12}>
                <Statistic 
                  title="Próximos Meses" 
                  value={monthlyValue} 
                  precision={2} 
                  prefix="R$"
                />
              </Col>
            </Row>
          )}
        </div>

        {/* Escolha da forma de pagamento (apenas para produtos com pagamento) */}
        {!isFreeProduct && (
          <div>
            <h4>Forma de Pagamento</h4>
            <div style={{ padding: 16, background: '#f0f5ff', borderRadius: 8, border: '1px solid #d6e4ff', textAlign: 'center' }}>
              <BarcodeOutlined style={{ fontSize: 32, color: '#1890ff', marginBottom: 8 }} />
              <p style={{ fontSize: 16, fontWeight: 'bold', margin: '8px 0' }}>Boleto Bancário</p>
              <p style={{ fontSize: 13, color: '#666', margin: 0 }}>
                Outras formas de pagamento (PIX e Cartão) serão habilitadas em breve.
              </p>
            </div>
          </div>
        )}

        {/* Informação sobre dados */}
        {!isFreeProduct && (
          <div style={{ padding: 12, background: '#fffbe6', borderRadius: 6, border: '1px solid #ffe58f' }}>
            <DollarOutlined style={{ marginRight: 8, color: '#faad14' }} />
            <span style={{ fontSize: 13 }}>
              Você será redirecionado para o checkout seguro do Asaas para concluir o pagamento.
            </span>
          </div>
        )}

        {/* Botões de ação */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={handleClose} disabled={isProcessing}>
            Cancelar
          </Button>
          <Button
            type="primary"
            onClick={handleFinish}
            loading={isProcessing}
            disabled={isProcessing}
            icon={isFreeProduct ? undefined : <BarcodeOutlined />}
          >
            {isProcessing ? 'Processando...' : isFreeProduct ? 'Ativar Produto' : 'Gerar Boleto'}
          </Button>
        </div>
      </Space>
    </Modal>
  );
}
